import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";

import {
  getParentStatus,
  getPlacementApplicationSummariesQuerySchema,
  type PlacementApplicationStatus,
  type PlacementApplicationSummariesResponse,
  PlacementApplicationSummariesSchema,
} from "../types/fetchPlacementApplications.schema";
import { type GetPlacementApplicationsForWorkplaceQuery } from "./useFetchPaginatedPlacementApplications";

interface GetPlacementApplicationSummaryParams {
  workplaceId: string;
  queryParams: GetPlacementApplicationsForWorkplaceQuery;
}

export function useGetPlacementApplicationSummary<
  SelectData = PlacementApplicationSummariesResponse
>(
  params: GetPlacementApplicationSummaryParams,
  options?: UseGetQueryOptions<PlacementApplicationSummariesResponse, SelectData>
) {
  const { workplaceId, queryParams } = params;

  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/placement-application-summaries`,
    queryParams,
    requestSchema: getPlacementApplicationSummariesQuerySchema,
    responseSchema: PlacementApplicationSummariesSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_PLACEMENT_APPLICATION_SUMMARY_FAILURE,
      userErrorMessage: "Something went wrong while loading the job application summary.",
    },
    ...options,
  });
}

interface TransformPlacementApplicationSummaryOptions {
  isAdmin?: boolean;
}

export function transformPlacementApplicationSummary(
  data?: PlacementApplicationSummariesResponse,
  options: TransformPlacementApplicationSummaryOptions = {}
) {
  if (!data) {
    return undefined;
  }

  const { isAdmin = false } = options;
  const { attributes } = data.data;
  const aggregatedAttributes: Record<PlacementApplicationStatus, number> = { ...attributes };

  // Aggregate counts for main status with grouped statuses
  Object.entries(attributes).forEach(([key, value]) => {
    if (key.includes(":")) {
      // Skip interview:pending for non-admins
      if (!isAdmin && key === "interview:pending") {
        return;
      }
      const parentStatus = getParentStatus(key as PlacementApplicationStatus);
      aggregatedAttributes[parentStatus] = (aggregatedAttributes[parentStatus] || 0) + value;
    }
  });

  const allStatusCount = Object.values(attributes).reduce(
    (accumulator, current) => accumulator + current,
    0
  );

  return {
    ...aggregatedAttributes,
    allStatusCount,
  };
}

export type PlacementApplicationSummaryWithAllStatusCount = ReturnType<
  typeof transformPlacementApplicationSummary
>;
